import { Link } from "react-router-dom";
import { Wallet, Search, MessageCircle, Star, ShieldCheck, Clock, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

function Page({ title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <h1 className="font-serif text-3xl text-celeste-ivoire sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 text-celeste-lilas/75">{subtitle}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

export function CommentCaMarche() {
  const steps = [
    { icon: Wallet, t: "1. Créez votre compte", d: "Inscription réservée aux personnes majeures, avec confirmation 18+. Vous recevez des crédits de bienvenue pour découvrir la plateforme." },
    { icon: Search, t: "2. Trouvez votre praticien", d: "Parcourez l'annuaire et filtrez par spécialité, langue, tarif, note et disponibilité immédiate." },
    { icon: MessageCircle, t: "3. Démarrez le chat", d: "Envoyez une demande à l'expert. La facturation à la minute ne débute qu'après son acceptation. Un compteur et votre solde restent toujours visibles." },
    { icon: Clock, t: "4. Maîtrisez votre budget", d: "Le minuteur et le débit sont calculés côté serveur. Vous êtes alerté avant l'épuisement du solde, et la session se termine automatiquement à zéro crédit." },
    { icon: Star, t: "5. Laissez un avis vérifié", d: "Après une consultation terminée, partagez votre ressenti. Votre avis porte la mention « Consultation vérifiée »." },
  ];
  return (
    <Page title="Comment ça marche" subtitle="Une guidance simple, transparente et maîtrisée.">
      <div className="space-y-4">
        {steps.map((s, i) => (
          <div key={i} className="glass flex items-start gap-4 rounded-3xl p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-celeste-or/30 bg-celeste-or/10"><s.icon className="h-6 w-6 text-celeste-or" /></div>
            <div><h3 className="font-serif text-lg text-celeste-ivoire">{s.t}</h3><p className="mt-1 text-sm text-celeste-lilas/80">{s.d}</p></div>
          </div>
        ))}
      </div>
      <div className="glass mt-6 rounded-3xl border border-celeste-or/20 p-5 text-sm text-celeste-lilas/80">
        <ShieldCheck className="mb-2 h-5 w-5 text-celeste-or" />
        Les guidances sont proposées à des fins de réflexion personnelle et de divertissement. Elles ne remplacent jamais un avis médical, psychologique, juridique ou financier et ne garantissent aucun résultat futur.
      </div>
    </Page>
  );
}

export function Tarifs() {
  return (
    <Page title="Tarifs & crédits" subtitle="Un système prépayé, sans abonnement ni renouvellement automatique.">
      <div className="glass rounded-3xl p-6 text-sm leading-relaxed text-celeste-lilas/85">
        <p>Les consultations sont réglées en <strong className="text-celeste-or">crédits prépayés</strong>. Vous achetez un pack de crédits, puis chaque praticien applique un tarif exprimé en <strong>crédits par minute</strong>, affiché sur sa fiche.</p>
        <ul className="mt-4 list-disc space-y-2 pl-5">
          <li>Aucun abonnement caché, aucun prélèvement récurrent.</li>
          <li>La facturation démarre uniquement après acceptation de la consultation par l'expert.</li>
          <li>Le décompte est calculé côté serveur, à la minute, avec un solde visible en permanence.</li>
          <li>Une alerte vous prévient avant l'épuisement du solde ; la session s'arrête automatiquement à zéro crédit.</li>
          <li>Paiement sécurisé via Stripe.</li>
        </ul>
        <p className="mt-4">Consultez les packs disponibles depuis votre <Link to="/credits" className="text-celeste-or hover:underline">portefeuille</Link>.</p>
      </div>

      <div className="glass mt-6 rounded-3xl border border-celeste-or/20 p-6 text-sm leading-relaxed text-celeste-lilas/85" data-testid="boutique-info">
        <h3 className="font-serif text-lg text-celeste-or">Boutique — Tirage de cartomancie (60 €)</h3>
        <p className="mt-2">Il s'agit d'un <strong>service de boutique distinct</strong> des consultations en direct : un tirage de cartomancie personnalisé, <strong>livré sous 48 à 72 h après paiement</strong>. Ce n'est <strong>pas une séance facturée à la minute</strong> et cela ne remplace pas les consultations par chat. Ce service ouvrira prochainement.</p>
      </div>

      <div className="glass mt-6 rounded-3xl border border-celeste-or/20 p-6 text-sm leading-relaxed text-celeste-lilas/85" data-testid="founding-recruitment">
        <h3 className="font-serif text-lg text-celeste-or">Vous êtes praticien ? Offre fondateurs</h3>
        <p className="mt-2">Les <strong>20 premiers praticiens réels</strong> inscrits bénéficient de <strong>55 % du montant des consultations reversés</strong> (avant frais de paiement), au lieu du taux standard. Avantage maintenu tant que le praticien reste inscrit. Profils de démonstration exclus. Candidature soumise à une vérification manuelle d'identité et de statut.</p>
      </div>
    </Page>
  );
}

const FAQ = [
  ["La voyance est-elle garantie ?", "Non. Les guidances sont proposées à des fins de réflexion personnelle et de divertissement. Elles ne remplacent aucun avis médical, psychologique, juridique ou financier et ne garantissent aucun résultat futur."],
  ["Quand suis-je facturé ?", "La facturation à la minute ne commence qu'après l'acceptation de votre demande par le praticien. Tant que la session n'est pas active, aucun crédit n'est débité."],
  ["Comment est calculé le temps ?", "Le minuteur et le débit sont calculés côté serveur, jamais dans votre navigateur, pour garantir l'exactitude. Chaque mouvement de crédits est inscrit dans un historique immuable."],
  ["Que se passe-t-il si mon solde est épuisé ?", "Vous recevez une alerte avant l'épuisement. À zéro crédit, la session se termine automatiquement et un récapitulatif vous est présenté."],
  ["Puis-je être remboursé ?", "En cas de litige, vous pouvez ouvrir un signalement depuis la salle de chat ou l'assistance. L'administration peut décider d'un remboursement en crédits après examen."],
  ["Mes échanges sont-ils privés ?", "Seuls vous et le praticien concerné accédez à votre conversation. Les accès administratifs éventuels sont journalisés. Les données transitent de manière chiffrée (HTTPS)."],
  ["Puis-je devenir praticien ?", "Oui, via une candidature soumise à une vérification manuelle d'identité et de statut par notre équipe avant toute mise en ligne."],
];

export function FaqPage() {
  return (
    <Page title="Questions fréquentes" subtitle="Tout ce qu'il faut savoir avant votre première guidance.">
      <Accordion type="single" collapsible className="glass rounded-3xl p-2 sm:p-4">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={i} value={`item-${i}`} className="border-celeste-violet/15">
            <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-celeste-ivoire hover:text-celeste-or"><span className="flex items-center gap-2"><HelpCircle className="h-4 w-4 text-celeste-or" /> {q}</span></AccordionTrigger>
            <AccordionContent className="text-celeste-lilas/80">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Page>
  );
}
